import { useContextValue } from 'app/shared/hooks/use-context-value';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';
import { HQO_BRAND, HqoHeadersEnum } from 'app/shared/consts/enums';
import { authTokenStore, STRIP_BEARER } from 'app/shared/auth';
import { createContext, FC, PropsWithChildren, useEffect, useMemo } from 'react';

export interface ApiContextValue {
  apiUrl: string;
  authToken: string;
  getAuthTokenValue: () => Promise<string>;
  getAuthHeaderValue: () => Promise<string>;
  getAuthHeaders: () => Promise<Record<string, string>>;
  getDefaultHeaders: (buildingUuid?: string) => Record<string, string>;
  /**
   * Returns the raw bearer token (no `Bearer ` prefix). The
   * `@hqo/service-booking-client` package's `AuthorizationOptions` expects the
   * token in this shape; the rest of the api-client wrapper still consumes
   * `getAuthHeaders` so non-SBS callers stay on the prebuilt header bag.
   */
  getAuthToken: () => Promise<string>;
}

const ApiContext = createContext<ApiContextValue>({
  apiUrl: process.env.REACT_APP_API_URL || '',
  authToken: '',
  getAuthTokenValue: () => Promise.resolve(''),
  getAuthHeaderValue: () => Promise.resolve(''),
  getAuthHeaders: () => Promise.resolve({}),
  getDefaultHeaders: () => ({}),
  getAuthToken: () => Promise.resolve(''),
});

const toAuthorizationHeader = (rawToken: string): string => (rawToken ? `Bearer ${rawToken}` : '');

export const ApiProvider: FC<PropsWithChildren> = ({ children }) => {
  const { authToken, apiUrl, appBrand, userBuildingUuid } = useOpenParams();

  // Seed the module-level store with the token the host handed us via the
  // iframe URL. From here on the store is the source of truth so a mid-session
  // SDK refresh (see `useAuthTokenSync`) transparently reaches every caller,
  // even though `ApiProvider` sits above `MiniappSdkProvider` and can't read
  // the SDK itself.
  useEffect(() => {
    authTokenStore.setToken(authToken);
  }, [authToken]);

  const contextValue = useMemo(() => {
    // Read the freshest token from the store (which waits for any in-flight
    // refresh), falling back to the open-params token before the store is
    // seeded on first render.
    const resolveRawToken = async (): Promise<string> =>
      (await authTokenStore.getToken()) || STRIP_BEARER(authToken);

    return {
      apiUrl: apiUrl ? new URL(apiUrl).origin : '',
      authToken,
      getAuthTokenValue: resolveRawToken,
      getAuthHeaderValue: async () => toAuthorizationHeader(await resolveRawToken()),
      getAuthHeaders: async () => ({ Authorization: toAuthorizationHeader(await resolveRawToken()) }),
      // Alias for `getAuthTokenValue` — exposed under the name the
      // `@hqo/service-booking-client` package expects so the auth-glue hook
      // can pass `{ authToken: await getAuthToken() }` without reaching for
      // the legacy name.
      getAuthToken: resolveRawToken,
      // Defaults to the user's building (from query `?buildingUuid=`). Callers
      // can pass a different uuid (e.g. the service's building from the route)
      // when an API call needs to be scoped to a different building.
      getDefaultHeaders: (buildingUuid?: string): Record<string, string> => ({
        [HqoHeadersEnum.BRAND]: appBrand ?? HQO_BRAND,
        ...((buildingUuid || userBuildingUuid)
          && { [HqoHeadersEnum.HQO_BUILDING_UUID]: buildingUuid || userBuildingUuid }),
      }),
    };
  }, [authToken, apiUrl, appBrand, userBuildingUuid]);

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>;
};

export const useApi = <K extends keyof ApiContextValue | undefined = undefined>(key?: K) =>
  useContextValue(ApiContext, key);
